import {
    ServiceItem,
    CustomerPrice,
    Customer,
    QuoteItem,
    QuoteSection,
    Quote,
    QuoteStatus,
    Report,
    Note,
    AppSettings,
    IntervalKind,
    IntervalPhase,
    QuoteTotalsMode,
} from '../types';
import type { AcceptQuoteValues } from './quoteAcceptDefaults';
import { isHourlyUnit } from './serviceUnits';
import { kopiereVorlage, pruefeDefinition } from './bestandteile/bestandteilLogic';
import { kontingentFuerKopie } from './bestandteile/kontingentKopie';

/**
 * Kaufmännische Rundung auf 2 Nachkommastellen (round half up).
 *
 * Float-Caveat: IEEE-754 stellt Werte wie 1.005 nicht exakt dar
 * (1.005 -> 1.00499999999999989...). Ein naives Math.round(x*100)/100 würde
 * 1.005 auf 1.00 abrunden. Der Number.EPSILON-Nudge korrigiert dies, sodass
 * `round2(1.005) === 1.01` und `round2(0.125) === 0.13` kaufmännisch korrekt sind.
 */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Effektiver Netto-Einzelpreis einer Position (nach positionsbezogenem Rabatt).
 * Nur fuer Per-Einheit-Darstellung/Uebernahme (z.B. Projekt-LV) – NICHT fuer den
 * Zeilenbetrag, siehe lineTotal.
 */
const effectiveLineUnitPrice = (item: Pick<QuoteItem, 'unitPrice' | 'discountPercent'>): number =>
    round2(item.unitPrice * (1 - (item.discountPercent ?? 0) / 100));

/**
 * Zeilenbetrag einer Position: erst multiplizieren (Menge * Einzelpreis * Rabatt),
 * dann EINMAL runden. So stimmt der angezeigte Zeilenbetrag im QuoteEditor immer
 * mit der aufsummierten Zwischensumme ueberein (kein doppeltes Runden ueber den
 * Einzelpreis, das je nach Menge abweichen kann).
 */
export const lineTotal = (item: Pick<QuoteItem, 'quantity' | 'unitPrice' | 'discountPercent'>): number =>
    round2(item.quantity * item.unitPrice * (1 - (item.discountPercent ?? 0) / 100));

/**
 * German label of a struck-through position, shown wherever an amount would
 * normally stand (quote PDF, stamped original LV).
 */
export const QUOTE_EXCLUDED_LABEL = 'entfällt';

/** Summen-Modus eines Angebots; alles Unbekannte ist der bisherige Gesamtbetrag. */
export const quoteTotalsMode = (q: { details?: { totalsMode?: string } | null }): QuoteTotalsMode => {
    const m = q.details?.totalsMode;
    return m === 'unitPrices' || m === 'monthly' ? m : 'total';
};

export const QUOTE_TOTALS_MODE_OPTIONS: { value: QuoteTotalsMode; label: string }[] = [
    { value: 'total', label: 'Gesamtsumme (Standard)' },
    { value: 'unitPrices', label: 'Nur Einheitspreise (ohne Endsumme)' },
    { value: 'monthly', label: 'Betrag je Monat' },
];

/**
 * Kurzer Zusatz hinter dem Betrag in Listen: bei "je Monat" ist netTotal ein
 * Monatsbetrag, bei "nur Einheitspreise" nur ein interner Schaetzwert
 * (Menge x Preis), der dem Kunden nie genannt wurde.
 */
export const quoteAmountSuffix = (q: { details?: { totalsMode?: string } | null }): string => {
    const m = quoteTotalsMode(q);
    return m === 'monthly' ? '/ Monat' : m === 'unitPrices' ? 'nur EP' : '';
};

/**
 * Prozentsatz fuers Dokument ohne Rundung auf wenige Stellen ("10", "2,5",
 * "33,333"). Ein gerundet ausgewiesener Rabatt wiche von der Abrechnung ab,
 * die mit dem gespeicherten Wert rechnet.
 */
export const fmtPercentDE = (n: number): string =>
    (Number.isFinite(n) ? n : 0).toLocaleString('de-DE', { maximumFractionDigits: 6 });

/**
 * A struck-through LV position: it stays in the list for reference but counts
 * in no total, consumes no position number and is not carried over into the
 * project LV when the quote is accepted. Fully reversible (toggle).
 *
 * Independent of `nep` (contingency position) and `locked` (LV given by the
 * client). A position may carry both flags; it is still dropped only once,
 * because every consumer filters the SAME list instead of subtracting sums.
 */
export const isQuoteItemExcluded = (it: Pick<QuoteItem, 'excluded'>): boolean => it.excluded === true;

/** Counterpart of isQuoteItemExcluded: does this position count? */
export const isQuoteItemActive = (it: Pick<QuoteItem, 'excluded'>): boolean => !isQuoteItemExcluded(it);

/**
 * The active (not struck-through) positions of a list. Generic so enriched
 * rows (e.g. carrying posNumber in the PDF pipeline) keep their type.
 *
 * Never use it for order-sensitive work on the full list (moveQuoteItem,
 * index-based matching against the original LV) — those need every row.
 */
export const activeQuoteItems = <T extends Pick<QuoteItem, 'excluded'>>(items: T[]): T[] =>
    items.filter(isQuoteItemActive);

/**
 * Einzelpreis einer Katalogposition unter Berücksichtigung von Kunden-Sonderpreisen.
 *
 * Präzedenz:
 *   1. override.unitPrice (fester Sonderpreis)
 *   2. override.discountPercent (positionsbezogener Rabatt auf den Katalogpreis)
 *   3. customerDefaultDiscount (kundenweiter Standardrabatt)
 *   4. Katalogpreis
 */
export const resolveUnitPrice = (
    catalogPrice: number,
    override?: CustomerPrice,
    customerDefaultDiscount?: number,
): number => {
    if (override?.unitPrice != null) {
        return round2(override.unitPrice);
    }
    if (override?.discountPercent != null) {
        return round2(catalogPrice * (1 - override.discountPercent / 100));
    }
    if (customerDefaultDiscount != null) {
        return round2(catalogPrice * (1 - customerDefaultDiscount / 100));
    }
    return round2(catalogPrice);
};

/**
 * Wandelt einen Leistungskatalog in Angebotspositionen um und wendet dabei die
 * Kunden-Sonderpreise an. `idFactory` erlaubt deterministische IDs (Tests);
 * per Default zählerbasiert und damit ohne Seiteneffekte reproduzierbar.
 */
export const applyCustomerPricing = (
    catalogItems: ServiceItem[],
    prices: CustomerPrice[],
    customer: Customer,
    idFactory?: () => string,
): QuoteItem[] => {
    let counter = 0;
    const nextId = idFactory ?? (() => `qi-${++counter}`);

    return catalogItems.map((item) => {
        const override = prices.find((p) => p.catalogItemId === item.id);
        // Bestandteile einer Paketposition reisen als Vorlage mit — als
        // KOPIE (Kernregel 8: eine spaetere Katalogaenderung aendert das
        // Angebot nicht). Intern, nie im Angebots-PDF. Ohne Vorlage kein
        // Schluessel, damit Altangebote beim Speichern nicht wachsen.
        // Das Stundenkontingent reist NUR mit ihnen (S1,
        // docs/stundenkontingent-vertrag.md) — ebenfalls intern, nie im PDF.
        const bestandteile = kopiereVorlage(item.bestandteile);
        return {
            id: nextId(),
            catalogItemId: item.id,
            lvPosition: item.lvPosition,
            title: item.title,
            unit: item.unit,
            quantity: 1,
            unitPrice: resolveUnitPrice(item.unitPrice, override, customer.defaultDiscountPercent),
            category: item.category,
            assignment: item.assignment,
            ...(bestandteile ? { bestandteile } : {}),
            ...kontingentFuerKopie({ bestandteile }, item.stundenKontingent),
        };
    });
};

export interface QuoteTotals {
    positionsTotal: number;
    discountAmount: number;
    netTotal: number;
    taxAmount: number;
    grossTotal: number;
}

/**
 * Interne Summenberechnung ueber eine bereits gefilterte Positionsliste.
 * Positionsrabatte fließen je Zeile ein, der Gesamtrabatt (`discountPercent`)
 * und die Steuer (`taxRate`) auf den Zwischensummen.
 */
const totalsOf = (
    items: QuoteItem[],
    discountPercent: number,
    taxRate: number,
): QuoteTotals => {
    const positionsTotal = round2(
        items.reduce((sum, item) => sum + lineTotal(item), 0),
    );
    const discountAmount = round2(positionsTotal * discountPercent / 100);
    const netTotal = round2(positionsTotal - discountAmount);
    const taxAmount = round2(netTotal * taxRate / 100);
    const grossTotal = round2(netTotal + taxAmount);
    return { positionsTotal, discountAmount, netTotal, taxAmount, grossTotal };
};

/**
 * Berechnet die Angebots-Endsummen OHNE NEP (Eventualpositionen).
 *
 * NEP = nicht eingerechnete (Eventual-)Position: Der Einzelpreis wird im PDF
 * ausgewiesen, fließt aber NICHT in positionsTotal/Rabatt/Steuer/Endsumme ein.
 * (Bewusste Verhaltensaenderung ggue. Phase 1, wo alle Positionen zaehlten.)
 *
 * Struck-through positions drop out here as well, see isQuoteItemExcluded.
 */
export const calculateQuoteTotals = (
    items: QuoteItem[],
    discountPercent: number,
    taxRate: number,
): QuoteTotals =>
    totalsOf(activeQuoteItems(items).filter((it) => !it.nep), discountPercent, taxRate);

export interface QuoteTotalsDetailed {
    perSection: { sectionId: string | null; subtotal: number }[];
    totalsExclNep: QuoteTotals;
    totalsInclNep: QuoteTotals;
}

/**
 * Detaillierte Angebotssummen fuer das Kalkulationsblatt:
 *  - perSection: Zwischensumme je Sektion (Reihenfolge der Ersterscheinung,
 *    NEP-Positionen ausgeschlossen; Positionen ohne Sektion unter sectionId null).
 *  - totalsExclNep: Endsummen ohne NEP (maßgeblich fuer die Rechnung).
 *  - totalsInclNep: gleiche Pipeline, aber inklusive NEP (nachrichtlich).
 *
 * Struck-through positions are dropped from all three: they are not offered
 * any more, so neither a section subtotal nor the informative incl.-NEP total
 * may carry them. A section left without an active position disappears from
 * perSection, exactly as an all-NEP section already does.
 */
export const calculateQuoteTotalsDetailed = (
    items: QuoteItem[],
    discountPercent: number,
    taxRate: number,
): QuoteTotalsDetailed => {
    // Zwischensummen je Sektion, NEP ausgeschlossen. Reihenfolge = erste
    // Erscheinung der Sektion in items (Positionen ohne Sektion => null).
    const active = activeQuoteItems(items);
    const order: (string | null)[] = [];
    const sums = new Map<string | null, number>();
    for (const it of active) {
        if (it.nep) continue;
        const key = it.sectionId ?? null;
        if (!sums.has(key)) {
            sums.set(key, 0);
            order.push(key);
        }
        sums.set(key, sums.get(key)! + lineTotal(it));
    }
    const perSection = order.map((sectionId) => ({
        sectionId,
        subtotal: round2(sums.get(sectionId)!),
    }));

    return {
        perSection,
        totalsExclNep: totalsOf(active.filter((it) => !it.nep), discountPercent, taxRate),
        totalsInclNep: totalsOf(active, discountPercent, taxRate),
    };
};

/**
 * Verschiebt eine Position um einen Platz innerhalb IHRER Sektion.
 *
 * Warum das noetig ist: die Positionsnummer entsteht aus der Reihenfolge in
 * `items` (computePosNumbers zaehlt je Sektion durch). Sortiert der LV-Import
 * eine Zeile falsch ein — und das kommt vor, die Reihenfolge stammt aus einer
 * KI-Antwort —, liess sie sich bisher nur ueber Loeschen und Neuanlegen
 * korrigieren. Die Sektion war wechselbar, die Reihenfolge darin nicht.
 *
 * Der Tausch findet mit dem NACHBARN DERSELBEN SEKTION statt, nicht mit dem
 * Nachbarn im Array: dazwischen koennen Positionen anderer Sektionen liegen,
 * und ein Tausch mit denen aenderte deren Nummerierung gleich mit.
 *
 * Rein: gibt eine neue Liste zurueck. Gibt es keinen Nachbarn (erste bzw.
 * letzte Position der Sektion) oder die id nicht, bleibt die Liste unveraendert
 * — der Aufrufer braucht keine Sonderbehandlung.
 */
export const moveQuoteItem = (
    items: QuoteItem[],
    itemId: string,
    delta: -1 | 1,
): QuoteItem[] => {
    const index = items.findIndex((it) => it.id === itemId);
    if (index < 0) return items;

    const sectionOf = (it: QuoteItem) => it.sectionId ?? null;
    const section = sectionOf(items[index]);

    // Nachbarindex derselben Sektion in Laufrichtung suchen.
    let neighbour = -1;
    for (let i = index + delta; i >= 0 && i < items.length; i += delta) {
        if (sectionOf(items[i]) === section) {
            neighbour = i;
            break;
        }
    }
    if (neighbour < 0) return items;

    const next = [...items];
    [next[index], next[neighbour]] = [next[neighbour], next[index]];
    return next;
};

/**
 * Erste bzw. letzte Position ihrer Sektion? Steuert die Pfeil-Schaltflaechen im
 * Editor, damit ein wirkungsloser Klick gar nicht erst moeglich ist.
 */
export const canMoveQuoteItem = (
    items: QuoteItem[],
    itemId: string,
    delta: -1 | 1,
): boolean => moveQuoteItem(items, itemId, delta) !== items;

/**
 * Vergleicht zwei LV-Nummern so, wie ein Leistungsverzeichnis gelesen wird:
 * Zahlenbloecke numerisch, alles dazwischen als Text. "2.13.10" steht damit
 * HINTER "2.4.20" — ein reiner Zeichenvergleich sortierte genau andersherum,
 * und eine Liste, in der die 13 vor der 4 kommt, liest sich wie ein Fehler.
 *
 * Eine leere Nummer sortiert ans Ende: eine frei angelegte Position hat keine
 * Nummer aus dem Kundendokument und soll die nummerierten nicht auseinander-
 * reissen.
 */
export const compareLvPosition = (a: string, b: string): number => {
    const sa = (a || '').trim();
    const sb = (b || '').trim();
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;

    const ta = sa.match(/\d+|\D+/g) ?? [];
    const tb = sb.match(/\d+|\D+/g) ?? [];
    for (let i = 0; i < Math.max(ta.length, tb.length); i += 1) {
        const xa = ta[i];
        const xb = tb[i];
        // Die kuerzere Nummer zuerst: "1.1" steht vor "1.1.10".
        if (xa === undefined) return -1;
        if (xb === undefined) return 1;
        if (/^\d+$/.test(xa) && /^\d+$/.test(xb)) {
            const d = Number(xa) - Number(xb);
            if (d !== 0) return d;
        } else {
            const d = xa.localeCompare(xb, 'de');
            if (d !== 0) return d;
        }
    }
    return 0;
};

/**
 * Ordnet die Positionen JE SEKTION nach der LV-Nummer des Kunden
 * (`item.lvPosition`).
 *
 * Warum je Sektion und nicht ueber die ganze Liste: die Sektionsbloecke sind
 * die Gliederung des Angebots, und `computePosNumbers` zaehlt in ihnen durch.
 * Sortiert man das Array am Stueck, wandern Positionen zwischen den Abschnitten
 * — die Reihenfolge der Abschnitte gehoert aber den Pfeilen an der Abschnitts-
 * ueberschrift, nicht dieser Funktion. Die belegten Plaetze einer Sektion
 * bleiben deshalb dieselben, nur ihre Belegung wird getauscht (genau wie bei
 * moveQuoteItem).
 *
 * Stabil: gleiche oder fehlende Nummern behalten ihre bisherige Reihenfolge.
 * Rein — und gibt bei bereits richtiger Reihenfolge die EINGABELISTE zurueck,
 * damit der Aufrufer eine wirkungslose Aktion erkennen (und den Knopf sperren)
 * kann.
 */
export const sortQuoteItemsByLvPosition = (items: QuoteItem[]): QuoteItem[] => {
    // Belegte Array-Plaetze je Sektion.
    const slots = new Map<string | null, number[]>();
    items.forEach((it, idx) => {
        const key = it.sectionId ?? null;
        const list = slots.get(key);
        if (list) list.push(idx);
        else slots.set(key, [idx]);
    });

    const next = [...items];
    let changed = false;
    slots.forEach((idxs) => {
        const sorted = idxs
            .map((i) => ({ i, it: items[i] }))
            .sort((x, y) => compareLvPosition(x.it.lvPosition || '', y.it.lvPosition || '') || x.i - y.i)
            .map((e) => e.it);
        idxs.forEach((slot, k) => {
            if (next[slot] !== sorted[k]) changed = true;
            next[slot] = sorted[k];
        });
    });
    return changed ? next : items;
};

/**
 * Wuerde ein Sortierlauf ueberhaupt etwas aendern? Steuert den Knopf im Editor,
 * damit ein wirkungsloser Klick gar nicht erst moeglich ist — dasselbe Muster
 * wie canMoveQuoteItem.
 */
export const canSortQuoteItemsByLvPosition = (items: QuoteItem[]): boolean =>
    sortQuoteItemsByLvPosition(items) !== items;

/**
 * Vergibt Positionsnummern im Firmen-Schema `${sIdx+1}.1.${(i+1)*10}` je Sektion
 * (1.1.10, 1.1.20, …; zweite Sektion 2.1.10, …). Positionen ohne (bzw. mit
 * unbekannter) Sektion werden hinter der letzten definierten Sektion einsortiert
 * (eigener Sektionsindex nach den bekannten). Reine Funktion (kein Seiteneffekt):
 * gibt eine neue Positionsliste mit gesetztem posNumber zurueck.
 *
 * A struck-through position keeps its place in the list but gets posNumber
 * undefined and does NOT advance the counter of its section: active positions
 * are numbered consecutively; struck positions carry no internal number, the
 * customer's LV number stays the reference. Striking one out therefore CLOSES
 * the gap — of 1.1.10/1.1.20/1.1.30 the third becomes 1.1.20 once the middle
 * one is struck. That is deliberate: a printed list with holes in its numbering
 * reads like a defect, the binding reference towards the customer is the LV
 * number of his own document (item.lvPosition), and striking is only possible
 * while the quote is a draft — nothing sent out can shift underneath anybody.
 * The section index is still claimed as before, so the numbering of OTHER
 * sections cannot shift either.
 */
export const computePosNumbers = (
    sections: QuoteSection[],
    items: QuoteItem[],
): QuoteItem[] => {
    // Sektionsindex je bekannter sectionId. Unbekannte/fehlende Sektionen
    // bekommen den naechsten freien Index nach den definierten Sektionen.
    const sectionIndex = new Map<string, number>();
    sections.forEach((s, idx) => sectionIndex.set(s.id, idx));
    let overflowIndex = sections.length;
    const overflowFor = new Map<string | null, number>();

    // Laufender Positionszaehler je Sektionsindex.
    const counters = new Map<number, number>();

    return items.map((item) => {
        const key = item.sectionId ?? null;
        let sIdx: number;
        if (key !== null && sectionIndex.has(key)) {
            sIdx = sectionIndex.get(key)!;
        } else if (overflowFor.has(key)) {
            sIdx = overflowFor.get(key)!;
        } else {
            sIdx = overflowIndex++;
            overflowFor.set(key, sIdx);
        }
        // No number for a struck position, and the counter stays put: the next
        // active position moves up into the freed number.
        if (isQuoteItemExcluded(item)) return { ...item, posNumber: undefined };
        const i = counters.get(sIdx) ?? 0;
        counters.set(sIdx, i + 1);
        return { ...item, posNumber: `${sIdx + 1}.1.${(i + 1) * 10}` };
    });
};

/**
 * Dokumentnummer, z.B. formatDocumentNumber('AN', 2026, 1) -> 'AN-2026-001'.
 * Sequenzen >= 1000 werden nicht abgeschnitten.
 */
export const formatDocumentNumber = (kind: 'AN' | 'AB', year: number, seq: number): string =>
    `${kind}-${year}-${String(seq).padStart(3, '0')}`;

/**
 * Die Typziffer aus einer Belegnummer im Projektnummern-Schema
 * (<Praefix>-JJMMTNNN, z.B. 'A-26081001' -> '1'). 1 = SiGeKo, 2 = SiFa,
 * 3 = Sonstige.
 *
 * Gibt undefined zurueck, wenn die Nummer nicht diesem Schema folgt — etwa bei
 * Altnummern ('A 441-2026', 'RE-2026-0001'). Der Aufrufer entscheidet dann
 * selbst, was gelten soll; geraten wird hier nichts.
 */
export const typeDigitFromDocumentNumber = (
    documentNumber?: string,
): '1' | '2' | '3' | undefined => {
    const m = /^[A-Za-z]+-\d{4}([123])\d{3,}$/.exec((documentNumber ?? '').trim());
    return m ? (m[1] as '1' | '2' | '3') : undefined;
};

/**
 * Nachfassdatum als yyyy-mm-dd. Arithmetik in UTC, um Zeitzonen-Drift zu vermeiden.
 */
export const computeFollowUpDate = (sentAtIso: string, followUpDays: number): string => {
    const d = new Date(sentAtIso);
    const utc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + followUpDays);
    const r = new Date(utc);
    const yyyy = r.getUTCFullYear();
    const mm = String(r.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(r.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

/**
 * sent_to-Kennung fuer ein Angebot, das im Vergabeportal hochgeladen statt
 * gemailt wurde. Eine E-Mail-Adresse beginnt nie mit "Vergabeportal" — die
 * Kennung ist damit eindeutig, ohne eigene Spalte.
 */
export const PORTAL_SENT_TO = 'Vergabeportal';

export const portalSentTo = (portal: string): string => {
    const p = portal.trim();
    return p ? `${PORTAL_SENT_TO}: ${p}` : PORTAL_SENT_TO;
};

export const isPortalSubmission = (sentTo?: string): boolean =>
    sentTo === PORTAL_SENT_TO || !!sentTo?.startsWith(`${PORTAL_SENT_TO}: `);

/**
 * Der Folgetag eines yyyy-mm-dd-Datums, UTC-sicher.
 *
 * Fuer anschliessende Phasenzeitraeume: endet Phase 1 am 15.01., beginnt
 * Phase 2 am 16.01. — dasselbe Datum als Start liesse die Zeitraeume einen
 * Tag ueberlappen und zaehlte Begehungen doppelt. Unbrauchbare Eingaben
 * liefern '' statt "Invalid Date" ins Formularfeld zu schreiben.
 */
export const nextDayIso = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return '';
    const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(Date.UTC(y, mo - 1, day));
    // Date.UTC normalisiert unmoegliche Kalendertage stillschweigend
    // (31.02. -> 03.03.) statt zu scheitern. Nur die Rueckpruefung der
    // Bestandteile erkennt das — NaN deckt sie mit ab (NaN !== y).
    if (d.getUTCFullYear() !== y || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== day) return '';
    return computeFollowUpDate(d.toISOString(), 1);
};

/**
 * Ein Angebot gilt als abgelaufen, wenn es versendet wurde ('sent') und das
 * Gültigkeitsdatum vor heute liegt. Vergleich rein auf dem Datumsanteil.
 */
export const isQuoteExpired = (
    q: { status: QuoteStatus; validUntil?: string },
    todayIso: string,
): boolean => {
    if (q.status !== 'sent' || !q.validUntil) return false;
    return q.validUntil.slice(0, 10) < todayIso.slice(0, 10);
};

const ALLOWED_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
    draft: ['sent', 'accepted', 'partial', 'rejected'],
    sent: ['accepted', 'partial', 'rejected', 'expired'],
    accepted: [],
    // Teilbeauftragt -> angenommen laeuft NUR ueber einen Auftragsteil
    // (quoteOrderService: Rest beauftragen / Rest entfaellt), nie ueber die
    // Annahme: die legte ein zweites Projekt an. Deshalb steht hier nichts —
    // jeder Knopf, der canTransition(…, 'accepted') fragt, bleibt aus.
    partial: [],
    rejected: [],
    expired: [],
};

/**
 * Erlaubte Statusübergänge. Selbstübergänge sind nicht erlaubt.
 */
export const canTransition = (from: QuoteStatus, to: QuoteStatus): boolean =>
    ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;

/**
 * Wandelt Angebotspositionen in Leistungspositionen (Projekt-LV) um.
 * Der Einzelpreis ist der effektive Netto-Preis nach Positionsrabatt.
 *
 * `minHoursByItemId` traegt die im Annahmedialog erfragten Mindeststunden
 * (QuoteItem.id -> Stunden) nach. Optional, damit bestehende Aufrufer
 * unveraendert bleiben; ohne Angabe entsteht wie bisher kein minHours. Nur
 * Stundenpositionen erhalten den Wert — an einer Stueckposition waere er eine
 * stille Falschaussage.
 *
 * Struck-through positions are not part of the order and therefore never
 * reach the project LV.
 *
 * NEP-Positionen (item.nep) kommen mit — sie stehen im Auftrag als
 * Eventualposition — tragen aber `is_optional`, damit sie in Leistung &
 * Budget als NEP erkennbar sind und eine Buchung darauf begruendet werden
 * muss. Ohne das Flag sah die Position im Projekt aus wie beauftragt.
 */
export const mapQuoteItemsToServiceItems = (
    items: QuoteItem[],
    minHoursByItemId?: Record<string, number>,
): ServiceItem[] =>
    activeQuoteItems(items).map((item) => {
        const min = minHoursByItemId?.[item.id];
        const carriesMin =
            isHourlyUnit(item.unit) && typeof min === 'number' && Number.isFinite(min) && min > 0;
        const service: ServiceItem = {
            id: item.id,
            lvPosition: item.lvPosition,
            title: item.title,
            unit: item.unit,
            quantityContracted: item.quantity,
            quantityUsed: 0,
            unitPrice: effectiveLineUnitPrice(item),
            status: 'open',
            category: item.category,
            assignment: item.assignment,
            ...(carriesMin ? { minHours: min } : {}),
            ...(item.nep ? { is_optional: true } : {}),
        };
        // PAKETPOSITION: die Bestandteile des Angebots werden in die
        // Projektposition KOPIERT (tief, normalisiert — Kernregel 8), aber nur,
        // wenn sie dort gueltig sind. Massgeblich ist die Menge, die hier
        // tatsaechlich ankommt (bei einer Teilbeauftragung die Menge des
        // Auftragsteils): Bestandteile gibt es nur bei Menge 1 (E5), und nie
        // auf einer Monatspauschale. Bei Menge 3 waere „alle Bestandteile
        // erledigt" nicht zu deuten (einmal? dreimal?), und eine Position mit
        // ungueltiger Definition sperrte das Speichern jedes Dialogs, der sie
        // prueft. Deshalb weglassen statt mitschleppen — dieselbe Regel wie
        // pruefeDefinition, keine eigene Lesart.
        //
        // Das Stundenkontingent des Angebots geht NUR mit, wenn die
        // Bestandteile hier ankommen (S1, docs/stundenkontingent-vertrag.md):
        // eine Teilbeauftragung mit Menge 0,5 bekommt weder Bestandteile noch
        // Kontingent. Ein ungueltiger Wert faellt weg.
        const bestandteile = kopiereVorlage(item.bestandteile);
        if (bestandteile && pruefeDefinition(service, bestandteile).ok) {
            service.bestandteile = bestandteile;
            Object.assign(service, kontingentFuerKopie(service, item.stundenKontingent));
        }
        return service;
    });

// ─── Begehungs-Intervalle (mehrphasig) ──────────────────────────────────────

/**
 * Anzeige-Labels der Intervall-Arten (deutsch).
 */
export const INTERVAL_LABELS: Record<IntervalKind, string> = {
    taeglich: 'täglich',
    woechentlich: 'wöchentlich',
    '14taeglich': '14-täglich',
    '2xmonat': '2x Monat',
    '1xmonat': '1x Monat',
    individuell: 'individuell',
};

/**
 * Uebliche Einheiten fuer Angebots-/Kalkulationspositionen. `value` wird 1:1 in
 * item.unit gespeichert (Datenmodell bleibt ein reiner String), `label` ist die
 * deutsche Anzeige im Dropdown. Werte ausserhalb der Liste bleiben ueber die
 * 'Andere…'-Option als Freitext moeglich.
 */
export const QUOTE_UNITS: { value: string; label: string }[] = [
    { value: 'Std', label: 'Std (Stunde)' },
    { value: 'pau', label: 'pau (pauschal)' },
    { value: 'Stück', label: 'Stück' },
    { value: 'Tag', label: 'Tag' },
    { value: 'Woche', label: 'Woche' },
    { value: 'Monat', label: 'Monat' },
    { value: 'Begehung', label: 'Begehung' },
    { value: 'km', label: 'km' },
    { value: 'm²', label: 'm²' },
];

/**
 * Inklusive Tagesspanne von..bis in ganzen Tagen (UTC, zeitzonensicher).
 * Ungueltige oder umgekehrte (bis < von) Zeitraeume ergeben 0.
 */
const phaseDaySpan = (from: string, to: string): number => {
    const a = Date.parse(`${(from || '').slice(0, 10)}T00:00:00Z`);
    const b = Date.parse(`${(to || '').slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
    return Math.floor((b - a) / 86400000) + 1;
};

/**
 * Zaehlt die Werktage (Mo–Fr) in der inklusiven Spanne von..bis (UTC).
 */
const workingDays = (from: string, to: string): number => {
    const a = Date.parse(`${(from || '').slice(0, 10)}T00:00:00Z`);
    const b = Date.parse(`${(to || '').slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
    let count = 0;
    for (let t = a; t <= b; t += 86400000) {
        const dow = new Date(t).getUTCDay(); // 0=So, 6=Sa
        if (dow !== 0 && dow !== 6) count++;
    }
    return count;
};

/**
 * Anzahl Begehungen einer Phase nach Intervall-Art. Basis ist die inklusive
 * Tagesspanne; Wochen = Tage/7, Monate = Tage/30.44 (mittlere Monatslaenge).
 * Ergebnis nie negativ. Referenzfall der Firma: 1066 Tage (≈153 Wochen),
 * 14-täglich → ceil(153/2) = 77.
 */
export const phaseInspectionCount = (phase: IntervalPhase): number => {
    const days = phaseDaySpan(phase.from, phase.to);
    if (days <= 0) return 0;
    const weeks = days / 7;
    const months = days / 30.44;
    switch (phase.interval) {
        case 'taeglich':
            return workingDays(phase.from, phase.to);
        case 'woechentlich':
            return Math.ceil(weeks);
        case '14taeglich':
            return Math.ceil(weeks / 2);
        case '2xmonat':
            return Math.ceil(months * 2);
        case '1xmonat':
            return Math.ceil(months);
        case 'individuell':
            return Math.max(0, Math.ceil(weeks * (phase.perWeek ?? 0)));
        default:
            return 0;
    }
};

/**
 * Summe der Begehungsanzahl ueber alle Phasen.
 */
export const totalInspectionCount = (phases: IntervalPhase[]): number =>
    (phases || []).reduce((sum, p) => sum + phaseInspectionCount(p), 0);

/**
 * Schreibt die Mengen verknuepfter Angebotspositionen aus den Begehungs-Phasen
 * fort. Fuer jede Position, auf die >= 1 Phase verweist (phase.linkedItemId):
 *   quantity = ceil( Σ phaseInspectionCount(phase) * (phase.factor ?? 1) )
 * ueber alle auf diese Position verlinkten Phasen. Positionen ohne Verknuepfung
 * bleiben unveraendert. Reine Funktion (neue Liste, keine Mutation).
 *
 * Referenzfall der Firma (Pos. 1.2.30 'Sicherheitsbegehungen'): eine Phase mit
 * 77 Begehungen und factor 2.5 → ceil(77 * 2.5) = ceil(192.5) = 193 Std.
 *
 * Deliberately NOT filtered by isQuoteItemActive: this only keeps a quantity
 * in step with its phases, it sums nothing. Striking a position out is
 * reversible, so its quantity has to stay current — skipping it would leave a
 * stale number behind that silently reappears once the position is restored.
 * The amount is dropped where it matters, in the totals.
 */
export const syncPhaseQuantities = (
    items: QuoteItem[],
    phases: IntervalPhase[],
): QuoteItem[] => {
    // Summe der (Begehungen * factor) je verlinkter Position aufaddieren; erst
    // ueber alle Phasen summieren, dann EINMAL aufrunden (ceil auf die Summe).
    const sums = new Map<string, number>();
    for (const p of phases || []) {
        if (!p.linkedItemId) continue;
        const contrib = phaseInspectionCount(p) * (p.factor ?? 1);
        sums.set(p.linkedItemId, (sums.get(p.linkedItemId) ?? 0) + contrib);
    }
    if (sums.size === 0) return items;
    return items.map((it) =>
        sums.has(it.id) ? { ...it, quantity: Math.ceil(sums.get(it.id)!) } : it,
    );
};

// yyyy-mm-dd -> dd.mm.yyyy (reine Stringumformung, ohne Date-Parsing).
const fmtDE = (iso: string): string => {
    const [yyyy, mm, dd] = (iso || '').slice(0, 10).split('-');
    return dd && mm && yyyy ? `${dd}.${mm}.${yyyy}` : (iso || '');
};

/**
 * Deutsche Einzeiler-Zusammenfassung der Phasen, mit ', ' verbunden:
 *   `dd.mm.yyyy–dd.mm.yyyy <Label>` je Phase; 'individuell' als `Nx Woche`.
 * Leeres Array → ''.
 */
export const intervalSummary = (phases: IntervalPhase[]): string =>
    (phases || [])
        .map((p) => {
            const label =
                p.interval === 'individuell'
                    ? `${p.perWeek ?? 0}x Woche`
                    : INTERVAL_LABELS[p.interval];
            return `${fmtDE(p.from)}–${fmtDE(p.to)} ${label}`;
        })
        .join(', ');

/**
 * Erzeugt aus einem angenommenen Angebot einen Projekt-Report – spiegelt die
 * Struktur aus handleSetupComplete (App.tsx) wider (leere items/checklists,
 * globalData mit Status 'planning', übernommene Leistungen und Systemnotiz).
 */
export const quoteToReport = (
    quote: Quote,
    customer: Customer,
    coordinator: { id: string; name?: string; first_name?: string; last_name?: string; email?: string; phone?: string },
    projectNumber: string,
    settings: any,
    overrides?: Partial<AcceptQuoteValues>,
): Report => {
    const coordName =
        coordinator.name ||
        [coordinator.first_name, coordinator.last_name].filter(Boolean).join(' ') ||
        '';

    const primaryContact = customer.contacts?.[0]?.name || '';

    // Projekttyp aus dem Dialog; ohne Angabe wie bisher kein Typ (= SiGeKo).
    // Eine SiFa-Betreuung entsteht wie im Anlage-Assistenten
    // (ProjectSetupWizard/App.handleSetupComplete): laufend statt in Planung,
    // ohne Begehungsturnus, Ende leer = unbefristet.
    const projectType = overrides?.projectType;
    const sifa = projectType === 'sifa';

    const systemNote: Note = {
        // Reine UUID, kein Praefix: project_notes.id ist uuid, ein
        // "note-quote-<id>" liess save_project_children_tx mit 22P02 abbrechen
        // und damit die gesamte Auftragsannahme scheitern. Ein stabiler
        // Schluessel je Angebot bringt hier nichts – jede Annahme legt ohnehin
        // ein frisches Projekt an.
        id: crypto.randomUUID(),
        text: `Automatisch aus Angebot ${quote.quoteNumber} (Auftrag ${quote.orderNumber}) erstellt.`,
        date: quote.quoteDate || quote.createdAt || '',
        author: 'System',
        mentions: [],
        readBy: [],
    };

    return {
        metadata: {
            projectNumber,
            projectName: overrides?.projectName || quote.title || `${customer.name} – ${quote.quoteNumber}`,
            projectAddress: overrides?.address || '',
            ...(projectType ? { projectType } : {}),
            inspectionNumber: 0,
            date: '',
            time: '08:00',
            conductedBy: coordName,
            coordinator: coordName,
            coordinatorId: coordinator.id,
            coordinatorEmail: coordinator.email || '',
            coordinatorPhone: coordinator.phone || '',
            clientName: customer.name,
            clientAddress: customer.address,
            clientContactPerson: primaryContact,
            clientPhone: customer.phone,
            clientEmail: customer.email,
            customerName: customer.name,
            customerAddress: customer.address,
            customerContactPerson: primaryContact,
            customerPhone: customer.phone,
            customerEmail: customer.email,
            distributionList: [],
            plans: [],
            intervalConfig: sifa ? undefined : overrides?.intervalConfig,
            globalData: {
                projectId: 'temp-id',
                status: sifa ? 'active' : 'planning',
                startDate: overrides?.startDate,
                endDate: sifa ? (overrides?.endDate || undefined) : overrides?.endDate,
                services: mapQuoteItemsToServiceItems(quote.items, overrides?.minHoursByItemId),
                serviceLog: [],
                notes: [systemNote],
                // Ohne Koordinaten taucht das Projekt in keinem Umfeld-Check auf;
                // der Annahmedialog geocodiert die Baustellenadresse dafuer.
                locationCoordinates: overrides?.coordinates,
            },
        },
        settings: settings as AppSettings,
        items: [],
        checklists: [],
    };
};
